import type React from 'react'
import { useCallback, useRef, useState } from 'react'

import * as s from './LayoutScrollBorders.module.scss'

type Borders = {
  start: boolean
  end: boolean
}

const calcBorder = (
  node: HTMLDivElement,
  borderDirection?: 'vertical' | 'horizontal',
): Borders | undefined => {
  if (borderDirection === 'vertical') {
    // Используем допуск в 1px для более надежной проверки
    const isScrollable = node.scrollHeight > node.clientHeight + 1
    if (isScrollable) {
      return {
        start: node.scrollTop > 0.5,
        end: node.scrollHeight - node.scrollTop - node.clientHeight > 0.5,
      }
    }
  }
  if (borderDirection === 'horizontal') {
    const isScrollable = node.scrollWidth > node.clientWidth + 1
    if (isScrollable) {
      return {
        start: node.scrollLeft > 0.5,
        end: node.scrollWidth - node.scrollLeft - node.clientWidth > 0.5,
      }
    }
  }
  return undefined
}

const getBorderClass = (direction?: 'vertical' | 'horizontal', type?: 'solid' | 'shadow') => {
  if (type === 'solid') {
    if (direction === 'vertical') {
      return s.borders_vertical
    }
    if (direction === 'horizontal') {
      return s.border_horizontal
    }
  }
  if (type === 'shadow') {
    if (direction === 'vertical') {
      return s.shadow_vertical
    }
    if (direction === 'horizontal') {
      return s.shadow_horizontal
    }
  }
  return undefined
}

export const useScrollBorders = ({
  direction,
  type,
}: {
  direction?: 'vertical' | 'horizontal'
  type?: 'solid' | 'shadow'
}) => {
  const [borders, setBorders] = useState<Borders | undefined>()
  const refValue = useRef<HTMLDivElement | null>(null)
  const onScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      const target = e?.target as HTMLDivElement
      if (target) {
        setBorders(calcBorder(target, direction))
      }
    },
    [direction],
  )

  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      refValue.current = node
      if (node) {
        setBorders(calcBorder(node, direction))
      }
    },
    [direction],
  )

  const style = {
    '--scroll-border-before-opacity': borders?.start ? '1' : '0',
    '--scroll-border-after-opacity': borders?.end ? '1' : '0',
  } as React.CSSProperties
  return { borders, onScroll, cn: getBorderClass(direction, type), style, refValue, ref } as const
}
